import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MAX_OPEN_TABS, STORAGE_KEY } from '../constants';
import type { BlockOutput, SuggestedAction, TranscriptMessage } from '../types';

export type BarMode = 'docked' | 'floating' | 'fullscreen' | 'minimized';

export interface OpenTab {
  sessionId: string;
  title: string;
  status: 'idle' | 'streaming' | 'done' | 'error';
  createdAt: string;
}

export interface ServerMessageInput {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  /** Pre-assembled output blocks for assistant turns. The api adapter folds
   * tool-message metadata (structured_data) into the following assistant
   * turn so the rich UI survives a page refresh. Empty for user turns. */
  outputs?: BlockOutput[];
}

// One row inside the "Thinking…" card — a tool call or pipeline step.
export interface StreamingSubStep {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'done' | 'error';
  durationMs?: number;
}

// The assistant's reply while it's still being written. `content` grows
// with each stream_chunk; `subSteps` fills as tools start and finish.
// `outputs` collects rich blocks that tool_result events produce
// (candidate lists, action results, etc.) — they survive into the final
// committed message alongside the streamed text. Cleared at run end.
export interface StreamingDraft {
  sessionId: string;
  content: string;
  subSteps: StreamingSubStep[];
  outputs: BlockOutput[];
  /** Position in the session's transcript where the eventual assistant
   * message should land. Captured when the draft is first opened so that a
   * user-initiated message during streaming (e.g. a chip click) inserts
   * AFTER the assistant's reply rather than before it. */
  insertAt: number;
}

interface AgentForgeState {
  openTabs: OpenTab[];
  activeSessionId: string | null;
  mode: BarMode;
  historyOpen: boolean;
  // Linear-style "New chat" state: panel renders an empty composer without
  // committing a tab. The tab + session is created on first send.
  draftOpen: boolean;
  messagesBySession: Record<string, TranscriptMessage[]>;
  // The reply currently being streamed in. `null` when no run is active.
  streamingDraft: StreamingDraft | null;
  // Per-block row selection, keyed by the originating tool_call_id of the
  // card. Empty / missing entry means "no explicit selection" — chip
  // actions fall back to acting on all visible rows. The UI is the source
  // of truth for user intent; chip prompt templates carry placeholders
  // ({ids}, {count}, {first_id}) that the FE substitutes at submit time.
  selectionByBlock: Record<string, string[]>;
}

interface AgentForgeActions {
  openTab: (_tab: OpenTab) => void;
  closeTab: (_sessionId: string) => void;
  setActive: (_sessionId: string | null) => void;
  setTabStatus: (_sessionId: string, _status: OpenTab['status']) => void;
  setTabTitle: (_sessionId: string, _title: string) => void;
  setMode: (_mode: BarMode) => void;
  toggleHistory: (_open?: boolean) => void;
  openDraft: () => void;
  closeDraft: () => void;
  reset: () => void;
  appendUserMessage: (_sessionId: string, _content: string) => void;
  // Replaces the entire transcript with what the server reports. Used by
  // the polling hook to mirror `GET /sessions/{id}/messages` into the store.
  hydrateMessages: (
    _sessionId: string,
    _serverMessages: ServerMessageInput[]
  ) => void;
  // WS streaming-draft actions. Each takes a sessionId so frames meant for
  // another (older) session are silently ignored — see the implementations.
  appendStreamDelta: (_sessionId: string, _delta: string) => void;
  pushSubStep: (_sessionId: string, _step: StreamingSubStep) => void;
  patchSubStep: (
    _sessionId: string,
    _id: string,
    _patch: Partial<Omit<StreamingSubStep, 'id'>>
  ) => void;
  // Append a rich block (candidate list, action result, etc.) produced by
  // a tool_result. Block order matches the order tools complete.
  pushOutputBlock: (_sessionId: string, _block: BlockOutput) => void;
  // Patch the suggested-action chips on a block identified by tool_call_id.
  // Touches both the live streaming draft (current turn) and any committed
  // messages (prior turns or hydrated history) so smart chips replace
  // static defaults wherever the block lives.
  patchBlockChips: (
    _sessionId: string,
    _toolCallId: string,
    _chips: SuggestedAction[]
  ) => void;
  clearStreamingDraft: () => void;
  // Promote the streaming draft into a permanent assistant message on the
  // session's transcript and clear the draft. Used while the HTTP hydrate
  // is bypassed — the WS frames become the source of truth and we
  // self-commit at run-end so the streamed text doesn't keep growing on
  // the next turn.
  commitStreamingDraft: () => void;

  // Per-card row selection. Toggle adds/removes a single id; setBlockSelection
  // replaces the full set (used by select-all / clear-all in the card header).
  toggleBlockSelection: (_toolCallId: string, _id: string) => void;
  setBlockSelection: (_toolCallId: string, _ids: string[]) => void;
  clearBlockSelection: (_toolCallId: string) => void;
}

const INITIAL_STATE: AgentForgeState = {
  openTabs: [],
  activeSessionId: null,
  mode: 'docked',
  historyOpen: false,
  draftOpen: false,
  messagesBySession: {},
  streamingDraft: null,
  selectionByBlock: {},
};

const dropOldestUntilUnderLimit = (tabs: OpenTab[]): OpenTab[] =>
  tabs.length <= MAX_OPEN_TABS ? tabs : tabs.slice(tabs.length - MAX_OPEN_TABS);

const upsertTab = (tabs: OpenTab[], next: OpenTab): OpenTab[] => {
  const idx = tabs.findIndex((t) => t.sessionId === next.sessionId);
  if (idx === -1) return dropOldestUntilUnderLimit([...tabs, next]);
  const updated = [...tabs];
  updated[idx] = next;
  return updated;
};

// Suppress resume blocks that precede a dataTable in the same assistant turn.
// The LLM frequently fetches kula_contact_resumes for each candidate as the
// data source for a comparison present_table call. The resulting message has
// resume cards (one per candidate) THEN the comparison table — the user sees
// three full profiles before the one card that actually answers their question.
// The dataTable is the canonical answer; the resumes were data-fetch noise.
// Keep this rule in sync with the same logic in pushOutputBlock so streaming
// and history views agree.
const suppressRedundantBlocks = (outputs: BlockOutput[]): BlockOutput[] => {
  const hasDataTable = outputs.some((b) => b.type === 'dataTable');
  if (!hasDataTable) return outputs;
  return outputs.filter((b) => b.type !== 'resume');
};

// Server → store adapter. Assistant outputs prefer the api adapter's
// pre-assembled `outputs` (which folds in tool-message structured_data)
// and fall back to a plain text block when none are supplied (older
// sessions / pure-text turns). Polling returns the full transcript so
// hydration replaces, never appends.
const adaptServerMessage = (m: ServerMessageInput): TranscriptMessage => {
  if (m.role === 'user') {
    return {
      role: 'user',
      id: m.id,
      content: m.content,
      createdAt: m.createdAt,
    };
  }
  const rawOutputs =
    m.outputs && m.outputs.length > 0
      ? m.outputs
      : m.content
        ? [{ type: 'text' as const, data: { content: m.content } }]
        : [];
  return {
    role: 'assistant',
    id: m.id,
    outputs: suppressRedundantBlocks(rawOutputs),
    createdAt: m.createdAt,
  };
};

export const useAgentForgeStore = create<AgentForgeState & AgentForgeActions>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      openTab: (tab) =>
        set((s) => ({
          openTabs: upsertTab(s.openTabs, tab),
          activeSessionId: tab.sessionId,
          mode: s.mode === 'minimized' ? 'floating' : s.mode,
          draftOpen: false,
        })),

      closeTab: (sessionId) =>
        set((s) => {
          const remaining = s.openTabs.filter((t) => t.sessionId !== sessionId);
          const wasActive = s.activeSessionId === sessionId;
          const { [sessionId]: _, ...remainingMessages } = s.messagesBySession;
          // If the closed tab is the one we were streaming, drop the draft.
          const isStreamingThisTab = s.streamingDraft?.sessionId === sessionId;
          return {
            openTabs: remaining,
            activeSessionId: wasActive
              ? (remaining[remaining.length - 1]?.sessionId ?? null)
              : s.activeSessionId,
            messagesBySession: remainingMessages,
            streamingDraft: isStreamingThisTab ? null : s.streamingDraft,
          };
        }),

      setActive: (sessionId) => set({ activeSessionId: sessionId }),

      setTabStatus: (sessionId, status) =>
        set((s) => ({
          openTabs: s.openTabs.map((t) =>
            t.sessionId === sessionId ? { ...t, status } : t
          ),
        })),

      setTabTitle: (sessionId, title) =>
        set((s) => ({
          openTabs: s.openTabs.map((t) =>
            t.sessionId === sessionId ? { ...t, title } : t
          ),
        })),

      setMode: (mode) => set({ mode }),

      toggleHistory: (open) =>
        set((s) => ({ historyOpen: open ?? !s.historyOpen })),

      openDraft: () =>
        set({ draftOpen: true, activeSessionId: null, mode: 'floating' }),

      closeDraft: () => set({ draftOpen: false }),

      reset: () => set(INITIAL_STATE),

      appendUserMessage: (sessionId, content) =>
        set((s) => {
          const existing = s.messagesBySession[sessionId] ?? [];
          const next: TranscriptMessage = {
            id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role: 'user',
            content,
            createdAt: new Date().toISOString(),
          };
          return {
            messagesBySession: {
              ...s.messagesBySession,
              [sessionId]: [...existing, next],
            },
          };
        }),

      hydrateMessages: (sessionId, serverMessages) =>
        set((s) => ({
          messagesBySession: {
            ...s.messagesBySession,
            [sessionId]: serverMessages.map(adaptServerMessage),
          },
        })),

      // Append a stream_chunk to the live draft. Opens a fresh draft on the
      // first chunk so callers don't have to seed one. Frames belonging to
      // a different session are dropped.
      appendStreamDelta: (sessionId, delta) =>
        set((s) => {
          const draft = s.streamingDraft;
          if (draft && draft.sessionId !== sessionId) return {};
          const insertAt = s.messagesBySession[sessionId]?.length ?? 0;
          return {
            streamingDraft: draft
              ? { ...draft, content: draft.content + delta }
              : {
                  sessionId,
                  content: delta,
                  subSteps: [],
                  outputs: [],
                  insertAt,
                },
          };
        }),

      // Add a sub-step row (tool start / action progress). If an entry with
      // the same id already exists, update it in place — tool_start followed
      // by tool_result reuses the id, so we'd otherwise show duplicate rows.
      pushSubStep: (sessionId, step) =>
        set((s) => {
          const draft = s.streamingDraft;
          if (draft && draft.sessionId !== sessionId) return {};
          const base = draft ?? {
            sessionId,
            content: '',
            subSteps: [],
            outputs: [],
            insertAt: s.messagesBySession[sessionId]?.length ?? 0,
          };
          const idx = base.subSteps.findIndex((x) => x.id === step.id);
          const subSteps =
            idx === -1
              ? [...base.subSteps, step]
              : base.subSteps.map((x, i) =>
                  i === idx ? { ...x, ...step } : x
                );
          return { streamingDraft: { ...base, subSteps } };
        }),

      // Append a rich block produced by a tool_result. Opens a fresh draft
      // if none exists (rare — usually appendStreamDelta has already done so).
      //
      // Redundancy rule: a dataTable block is the canonical answer for the
      // analytical turn that produced it. Any resume blocks that arrived in
      // the same turn beforehand were intermediate data fetches the LLM did
      // to BUILD that table. Showing them alongside the table is duplicate
      // noise (the user sees three full profiles + a comparison of the same
      // three people). When a dataTable arrives, drop the preceding resume
      // blocks so the table is the answer.
      pushOutputBlock: (sessionId, block) =>
        set((s) => {
          const draft = s.streamingDraft;
          if (draft && draft.sessionId !== sessionId) return {};
          const base = draft ?? {
            sessionId,
            content: '',
            subSteps: [],
            outputs: [],
            insertAt: s.messagesBySession[sessionId]?.length ?? 0,
          };
          const filtered =
            block.type === 'dataTable'
              ? base.outputs.filter((b) => b.type !== 'resume')
              : base.outputs;
          return {
            streamingDraft: {
              ...base,
              outputs: [...filtered, block],
            },
          };
        }),

      // Patch an existing sub-step (used by tool_result to flip status).
      // No-op if the draft is gone or the id isn't there.
      patchSubStep: (sessionId, id, patch) =>
        set((s) => {
          const draft = s.streamingDraft;
          if (!draft || draft.sessionId !== sessionId) return {};
          return {
            streamingDraft: {
              ...draft,
              subSteps: draft.subSteps.map((x) =>
                x.id === id ? { ...x, ...patch } : x
              ),
            },
          };
        }),

      // Swap the suggestedActions on every candidate/actionResult block
      // whose `toolCallId` matches — both in the live streaming draft and in
      // the session's committed transcript. Pure update; identity-stable
      // when nothing matched so React doesn't re-render unnecessarily.
      patchBlockChips: (sessionId, toolCallId, chips) =>
        set((s) => {
          const patchBlock = (b: BlockOutput): BlockOutput => {
            if (b.type !== 'candidate' && b.type !== 'actionResult') return b;
            if (b.data.toolCallId !== toolCallId) return b;
            return { ...b, data: { ...b.data, suggestedActions: chips } };
          };
          // Streaming draft (current turn).
          const draft = s.streamingDraft;
          const nextDraft =
            draft && draft.sessionId === sessionId
              ? { ...draft, outputs: draft.outputs.map(patchBlock) }
              : draft;
          // Committed transcript (prior turns).
          const existing = s.messagesBySession[sessionId];
          if (!existing) return { streamingDraft: nextDraft };
          const nextMessages = existing.map((m) =>
            m.role === 'assistant'
              ? { ...m, outputs: m.outputs.map(patchBlock) }
              : m
          );
          return {
            streamingDraft: nextDraft,
            messagesBySession: {
              ...s.messagesBySession,
              [sessionId]: nextMessages,
            },
          };
        }),

      toggleBlockSelection: (toolCallId, id) =>
        set((s) => {
          const current = s.selectionByBlock[toolCallId] ?? [];
          const next = current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id];
          return {
            selectionByBlock: { ...s.selectionByBlock, [toolCallId]: next },
          };
        }),

      setBlockSelection: (toolCallId, ids) =>
        set((s) => ({
          selectionByBlock: { ...s.selectionByBlock, [toolCallId]: ids },
        })),

      clearBlockSelection: (toolCallId) =>
        set((s) => {
          const { [toolCallId]: _drop, ...rest } = s.selectionByBlock;
          return { selectionByBlock: rest };
        }),

      clearStreamingDraft: () => set({ streamingDraft: null }),

      commitStreamingDraft: () =>
        set((s) => {
          const draft = s.streamingDraft;
          if (!draft) return { streamingDraft: null };
          // Nothing worth committing if there's no text AND no blocks.
          if (!draft.content && draft.outputs.length === 0) {
            return { streamingDraft: null };
          }
          const existing = s.messagesBySession[draft.sessionId] ?? [];
          // Assemble the message: text first (the LLM's prose summary),
          // then the rich blocks collected from tool_result events in order.
          const outputs: BlockOutput[] = [];
          if (draft.content) {
            outputs.push({ type: 'text', data: { content: draft.content } });
          }
          outputs.push(...draft.outputs);
          const committed: TranscriptMessage = {
            role: 'assistant',
            id: `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            outputs,
            createdAt: new Date().toISOString(),
            // Persist the action trail so the perplexity-style "sources"
            // footer survives once streaming ends. Only keep completed
            // (non-active) steps — active ones imply mid-stream and would
            // be confusing on a finished turn.
            actionTrail: draft.subSteps
              .filter((s) => s.status !== 'active')
              .map((s) => ({
                id: s.id,
                name: s.name,
                description: s.description,
                status: s.status,
                durationMs: s.durationMs,
              })),
          };
          // Splice at the anchor index captured when the draft opened. This
          // keeps the assistant reply directly after the user message that
          // triggered it even if newer user messages (e.g. a chip click) were
          // appended while streaming was still in flight.
          const idx = Math.min(Math.max(draft.insertAt, 0), existing.length);
          const next = [
            ...existing.slice(0, idx),
            committed,
            ...existing.slice(idx),
          ];
          return {
            messagesBySession: {
              ...s.messagesBySession,
              [draft.sessionId]: next,
            },
            streamingDraft: null,
          };
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        openTabs: s.openTabs,
        activeSessionId: s.activeSessionId,
        mode: s.mode,
        historyOpen: s.historyOpen,
        messagesBySession: s.messagesBySession,
        selectionByBlock: s.selectionByBlock,
      }),
    }
  )
);
